import {
  AlertTriangle,
  Lightbulb,
  TrendingDown,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import type { TFunction } from "i18next";
import {
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import {
  type GoogleAdsAdGroupItem,
  type GoogleAdsCampaignItem,
  type GoogleAdsKpiItem,
  type GoogleAdsPaginationMeta,
  type GoogleAdsPerformanceByAdItem,
  type GoogleAdsPerformanceTrendItem,
} from "src/services/GoogleAdsDashboardAPI";
import {
  calculateCpa,
  calculateCpc,
  calculateCtr,
} from "../../data/GoogleAdsMockData";
import {
  adjustGoogleAdsApiRoas,
  normalizeGoogleAdsMoneyValue,
} from "../../utils/GoogleAdsApiMoney";

export type GoogleAdsTab = "campaigns" | "adgroups" | "ads" | "alerts";

export type GoogleAdsLiveMetricKey =
  | "total spend"
  | "impressions"
  | "clicks"
  | "conversions"
  | "ctr"
  | "cpc"
  | "cpa"
  | "roas"
  | "impressions to clicks drop off"
  | "clicks to conversions drop off";

export type GoogleKpiTooltipKey =
  | "totalSpend"
  | "impressions"
  | "clicks"
  | "conversions"
  | "ctr"
  | "cpc"
  | "cpa"
  | "roas"
  | "dropOffImpressionsClicks"
  | "dropOffClicksConversions";

export type GoogleSectionHelpKey =
  | "performanceTrends"
  | "conversionFunnel"
  | "creativePerformance"
  | "roasByCampaign"
  | "spendDistributionByCampaign"
  | "campaignPerformance"
  | "adGroupPerformance"
  | "spendByAdGroup"
  | "performanceByAdNetwork"
  | "allAds"
  | "roasByAgeDevice";

export type GoogleKpiCard = {
  liveKey: GoogleAdsLiveMetricKey;
  tooltipKey: GoogleKpiTooltipKey;
  title: string;
  value: string;
  change: number | undefined;
  changeFormattedValue?: string;
  trend: Array<{ date: string; value: number }>;
  good: boolean;
  polarity: "higher-is-better" | "lower-is-better";
};

export type GoogleAdsCampaignSortKey =
  | "name"
  | "campaignType"
  | "startDate"
  | "endDate"
  | "interactions"
  | "spend"
  | "impressions"
  | "clicks"
  | "ctr"
  | "cpc"
  | "conversions"
  | "cpa"
  | "roas";

export type CampaignSummary = {
  id: string;
  campaignName: string;
  adNetworkType: string | null;
  startDate: string | null;
  endDate: string | null;
  interactions: number | null;
  spend: number | null;
  budgetFormattedValue: string | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  ctr: number | null;
  cpc: number | null;
  cpa: number | null;
  roas: number | null;
  adGroups: GoogleAdsAdGroupItem[];
};

export type GoogleAdsAdGroupSortKey =
  | "name"
  | "interactions"
  | "spend"
  | "impressions"
  | "clicks"
  | "ctr"
  | "cpc"
  | "conversions"
  | "cpa"
  | "roas";

export type GoogleAdsAdsSortKey =
  | "name"
  | "adId"
  | "type"
  | "spend"
  | "impressions"
  | "clicks"
  | "conversions"
  | "ctr"
  | "cpc"
  | "cpa"
  | "roas";

export const GOOGLE_ADS_TAB_IDS: GoogleAdsTab[] = ["campaigns", "adgroups", "ads"];

export const TABLE_PAGE_SIZE = 10;
export const TREND_PAGE_SIZE = 10;
// Top-N entries rendered in the ROAS / ad-group summary charts.
export const CHART_TOP_ENTRIES = 5;
export const SEARCH_DEBOUNCE_MS = 300;

export const EMPTY_PAGINATION_META: GoogleAdsPaginationMeta = {
  page: 1,
  page_size: TABLE_PAGE_SIZE,
  total_items: 0,
  total_pages: 1,
};

export const CAMPAIGN_SORT_FIELD: Partial<Record<GoogleAdsCampaignSortKey, string>> = {
  name: "campaign_name",
  campaignType: "ad_network_type",
  startDate: "start_date",
  endDate: "end_date",
  interactions: "interactions",
  spend: "spend",
  impressions: "impressions",
  clicks: "clicks",
  ctr: "ctr",
  cpc: "cpc",
  cpa: "cpa",
  conversions: "conversions",
  roas: "roas",
};

export const AD_GROUP_SORT_FIELD: Partial<Record<GoogleAdsAdGroupSortKey, string>> = {
  name: "ad_group_name",
  interactions: "interactions",
  spend: "spend",
  impressions: "impressions",
  clicks: "clicks",
  ctr: "ctr",
  cpc: "cpc",
  cpa: "cpa",
  conversions: "conversions",
  roas: "roas",
};

// The ad-name column is derived from the ad id, so both it and the id column
// sort by `ad_id` on the backend.
export const ADS_SORT_FIELD: Partial<Record<GoogleAdsAdsSortKey, string>> = {
  name: "ad_id",
  adId: "ad_id",
  type: "ad_network_type",
  spend: "spend",
  impressions: "impressions",
  clicks: "clicks",
  conversions: "conversions",
  ctr: "ctr",
  cpc: "cpc",
  cpa: "cpa",
  roas: "roas",
};

/** English labels for Lucide icon matcher — same order as KPI cards. */
export const GOOGLE_KPI_ICON_LABELS: string[] = [
  "Total Spend",
  "Impressions",
  "Clicks",
  "Conversions",
  "CTR",
  "CPC",
  "CPA",
  "ROAS",
  "Impressions to Clicks Drop-off",
  "Clicks to Conversions Drop-off",
];

export const GOOGLE_ALERT_CARD_DEFS: Array<{
  icon: LucideIcon;
  titleKey: "card1Title" | "card2Title" | "card3Title" | "card4Title";
  descKey: "card1Desc" | "card2Desc" | "card3Desc" | "card4Desc";
}> = [
  { icon: AlertTriangle, titleKey: "card1Title", descKey: "card1Desc" },
  { icon: TrendingUp, titleKey: "card2Title", descKey: "card2Desc" },
  { icon: TrendingDown, titleKey: "card3Title", descKey: "card3Desc" },
  { icon: Lightbulb, titleKey: "card4Title", descKey: "card4Desc" },
];

export const emptyGoogleAdsMetrics = {
  totalSpend: 0,
  impressions: 0,
  clicks: 0,
  conversions: 0,
  roas: 0,
};

/**
 * Column-header help for the Google Ads tables. `key` is a `*Table.*` label key,
 * so a column's label and its tooltip stay addressed by the same name.
 *
 * `level` selects the ad-group / ad wording for the columns whose definition
 * names the entity ("spent by the ad group…"). Columns that read the same at
 * every level (CTR, ROAS, Interactions) are authored once in the shared campaign
 * bundle and resolved by the fallback below.
 */
export const getGoogleColumnTooltip = (
  t: TFunction,
  key: string,
  level?: "adGroup" | "ad"
): DefinitionTooltipInfo => {
  const bases = [
    ...(level ? [`googleAdsReport.${level}ColumnTooltips`] : []),
    "googleAdsReport.columnTooltips",
  ];

  for (const base of bases) {
    const description = t(`${base}.${key}.description`, { defaultValue: "" });
    if (description) {
      const formula = t(`${base}.${key}.formula`, { defaultValue: "" });
      return formula ? { description, formula } : { description };
    }
  }

  // Present in neither bundle — fall through to i18next's own missing-key output.
  return { description: t(`googleAdsReport.columnTooltips.${key}.description`) };
};

export const missingValue = "-";

// Backend currency strings can be non-finite sentinels ("PKR nan", "PKR inf")
// or absent (null/undefined); show a dash instead of leaking those to users.
const NON_FINITE_CURRENCY_PATTERN = /\b(?:nan|inf(?:inity)?)\b/i;

export const displayFormattedCurrency = (formatted: string | null | undefined): string =>
  formatted && !NON_FINITE_CURRENCY_PATTERN.test(formatted) ? formatted : missingValue;

export const hasDisplayValue = (value: string | null | undefined) => {
  const normalized = value?.trim();
  return Boolean(normalized && normalized !== missingValue && normalized !== "—");
};

export function googleAdsTrendItemSpend(row: GoogleAdsPerformanceTrendItem): number {
  const raw = row.total_spend ?? row.spend;
  return normalizeGoogleAdsMoneyValue(raw).amount;
}

export function googleAdsTrendItemLabel(row: GoogleAdsPerformanceTrendItem): string {
  return row.date ?? row.report_date ?? row.day ?? "";
}

export const toApiSort = <T extends string>(
  sort: { key: string; direction: SortDirection } | null,
  fieldMap: Partial<Record<string, string>>,
  fallback: T
): T | undefined => {
  if (!sort) {
    return fallback;
  }
  const field = fieldMap[sort.key];
  if (!field) {
    return fallback;
  }
  return `${field}_${sort.direction}` as T;
};

const toNullableFiniteNumber = (value: unknown) => {
  // `Number(null) === 0`, so without this guard an explicit backend `null`
  // (a real "no data" / undefined-ratio sentinel) would be silently turned
  // into a fabricated `0` instead of being preserved as missing.
  if (value === null || value === undefined) {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const mapCampaignApiItem = (item: GoogleAdsCampaignItem): CampaignSummary => {
  const spendNorm =
    item.spend !== null && item.spend !== undefined
      ? normalizeGoogleAdsMoneyValue(item.spend)
      : null;

  return {
    // The API returns one row per campaign + network type, so the same
    // campaign_id can appear several times; names can also repeat across
    // different campaigns. Combining both keeps the row identity unique.
    id: `${item.campaign_id ?? item.campaign_name ?? "campaign"}-${item.ad_network_type ?? ""}`,
    campaignName: item.campaign_name ?? "-",
    adNetworkType: item.ad_network_type?.trim() || null,
    startDate: item.start_date ?? null,
    endDate: item.end_date ?? null,
    interactions: toNullableFiniteNumber(item.interactions),
    spend: spendNorm?.amount ?? toNullableFiniteNumber(item.spend),
    budgetFormattedValue: item.budgetFormattedValue?.trim() || null,
    impressions: toNullableFiniteNumber(item.impressions),
    clicks: toNullableFiniteNumber(item.clicks),
    conversions: toNullableFiniteNumber(item.conversions),
    ctr: toNullableFiniteNumber(item.ctr),
    cpc: toNullableFiniteNumber(item.cpc),
    cpa: toNullableFiniteNumber(item.cpa),
    roas: toNullableFiniteNumber(item.roas),
    adGroups: item.ad_groups ?? [],
  };
};

export const mapAdGroupApiItem = (item: GoogleAdsAdGroupItem, index: number) => {
  const spendNorm = normalizeGoogleAdsMoneyValue(item.spend);
  return {
    id: `${item.ad_group_id ?? item.ad_group_name}-${index}`,
    name: item.ad_group_name ?? `Ad group ${index + 1}`,
    adNetworkType: item.ad_network_type?.trim() || null,
    spend: spendNorm.amount,
    impressions: Number(item.impressions ?? 0),
    clicks: Number(item.clicks ?? 0),
    conversions: Number(item.conversions ?? 0),
    ctr: item.ctr ?? calculateCtr(Number(item.clicks ?? 0), Number(item.impressions ?? 0)),
    cpc: item.cpc ?? calculateCpc(spendNorm.amount, Number(item.clicks ?? 0)),
    cpa: item.cpa ?? calculateCpa(spendNorm.amount, Number(item.conversions ?? 0)),
    roas:
      item.roas !== undefined && item.roas !== null
        ? adjustGoogleAdsApiRoas(item.roas, spendNorm.roasScale)
        : 0,
    interactions: toNullableFiniteNumber(item.interactions),
  };
};

export const mapPerformanceByAdItem = (item: GoogleAdsPerformanceByAdItem, index: number) => {
  const spendNorm = normalizeGoogleAdsMoneyValue(item.total_spend);
  const spend = spendNorm.amount;
  const impressions = Number(item.impressions ?? 0);
  const clicks = Number(item.clicks ?? 0);
  const conversions = Number(item.conversions ?? 0);
  return {
    // The same ad_id can appear on several rows (one per network type),
    // so the index suffix keeps row ids unique.
    id: `${String(item.ad_id ?? "") || "ad"}-${index}`,
    adId: String(item.ad_id ?? ""),
    name: item.ad_id?.trim() ? `Ad ${item.ad_id}` : `Ad ${index + 1}`,
    type: item.ad_network_type?.trim() || missingValue,
    adGroupName: missingValue,
    campaignName: item.campaign_name ?? "",
    spend,
    // Prefer the backend's pre-formatted currency strings for display.
    spendFormatted: item.total_spendFormattedValue,
    cpcFormatted: item.cpcFormattedValue,
    cpaFormatted: item.cpaFormattedValue,
    impressions,
    clicks,
    conversions,
    ctr: item.ctr ?? calculateCtr(clicks, impressions),
    cpc: item.cpc ?? calculateCpc(spend, clicks),
    // The API sends `cpa: null` as a real "infinite/undefined" sentinel
    // (0 conversions or 0 spend — see cpaFormattedValue "PKR inf"/"PKR nan").
    // Only fall back to the derived formula when the field is absent
    // (undefined), so we don't render a fabricated "$0.00".
    cpa: item.cpa === null ? null : item.cpa ?? calculateCpa(spend, conversions),
    roas:
      item.roas !== undefined && item.roas !== null
        ? adjustGoogleAdsApiRoas(item.roas, spendNorm.roasScale)
        : 0,
  };
};

export type LiveAdRow = ReturnType<typeof mapPerformanceByAdItem>;
export type AdGroupRow = ReturnType<typeof mapAdGroupApiItem>;

export const getRoasToneClass = (value: number) => {
  if (value >= 4) {
    return dt.metricSuccess;
  }
  if (value >= 2) {
    return dt.metricWarning;
  }
  return dt.metricDanger;
};

const googleAdsCampaignTagLabel = (campaignName: string) => {
  if (campaignName.includes("Search")) {
    return "search";
  }
  if (campaignName.includes("Display")) {
    return "display";
  }
  if (campaignName.includes("Shopping")) {
    return "shopping";
  }
  if (campaignName.includes("Video")) {
    return "video";
  }
  return "performance max";
};

export function googleAdsCampaignChannelI18nKey(campaignName: string): string {
  const slug = googleAdsCampaignTagLabel(campaignName);
  if (slug === "performance max") {
    return "performanceMax";
  }
  if (
    slug === "search" ||
    slug === "display" ||
    slug === "shopping" ||
    slug === "video"
  ) {
    return slug;
  }
  return "unknown";
}

const normalizeGoogleAdsMetricKey = (value: string): GoogleAdsLiveMetricKey | null => {
  const normalized = value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");

  if (normalized === "total cost") {
    return "total spend";
  }

  if (
    normalized === "total spend" ||
    normalized === "impressions" ||
    normalized === "clicks" ||
    normalized === "conversions" ||
    normalized === "ctr" ||
    normalized === "click-through rate" ||
    normalized === "click through rate" ||
    normalized === "cpc" ||
    normalized === "cost per click" ||
    normalized === "cpa" ||
    normalized === "cost per acquisition" ||
    normalized === "roas" ||
    normalized === "return on ad spend" ||
    normalized === "impressions to clicks drop off" ||
    normalized === "clicks to conversions drop off"
  ) {
    if (normalized === "click-through rate" || normalized === "click through rate") {
      return "ctr";
    }
    if (normalized === "cost per click") {
      return "cpc";
    }
    if (normalized === "cost per acquisition") {
      return "cpa";
    }
    if (normalized === "return on ad spend") {
      return "roas";
    }
    return normalized;
  }

  return null;
};

export const buildGoogleAdsKpiLookup = (items: GoogleAdsKpiItem[]) =>
  items.reduce<Partial<Record<GoogleAdsLiveMetricKey, GoogleAdsKpiItem>>>((acc, item) => {
    const key =
      normalizeGoogleAdsMetricKey(item?.id ?? "") ??
      normalizeGoogleAdsMetricKey(item?.title ?? "");
    if (key) {
      acc[key] = item;
    }
    return acc;
  }, {});

/** True when the API sent a catalog/i18n key instead of human-readable copy (e.g. LABELS.GOOGLE_ADS.COST). */
export function isGoogleAdsApiKpiTitlePlaceholder(title: string): boolean {
  const s = title.trim();
  if (!s) {
    return true;
  }
  if (/^labels\./i.test(s)) {
    return true;
  }
  // Dotted SCREAMING_SNAKE/CONST paths, no spaces — typical leaked backend keys
  if (/^[A-Z0-9_.-]+$/.test(s) && s.includes(".")) {
    return true;
  }
  return false;
}
